// FIRST SILICON — M0 engine type contract.
// Normative reference: docs/ARCHITECTURE_M0.md §4.
// All state must remain JSON-safe: plain objects/arrays, finite numbers, strings, booleans, null.

export type NodeId = string; // "N1200" ... "N45"
export type MarketId = string; // "pc-graphics"
export type ProjectId = string; // "prj-<n>"

export type Discipline = "design" | "verification" | "implementation";
export const DISCIPLINES: readonly Discipline[] = ["design", "verification", "implementation"];

/** The analog/digital fork (design §5). Locked by the company's first project in a
 *  path-affine market; off-path work carries effort and requirements-fit penalties. */
export type CompanyPath = "digital" | "analog";
export type PathAffinity = CompanyPath | "neutral";

/** Company lifecycle stage (design §3; pivot spec §1). "garage" (P0) is the
 *  pre-incorporation phase — no company, no payroll, no cap table; accepting a
 *  seed term sheet IS the founding transaction and lands you in "angel". Legacy
 *  scenarios (no garage block) start incorporated at "angel" as before. The
 *  vc→growth gate is automatic (revenue at positive gross margin + 2 active
 *  design wins); growth→ipo is player-initiated (fileS1); ipo resolves to
 *  public (priced) or back to growth (pulled). Stages never regress otherwise. */
export type CompanyStage = "garage" | "angel" | "vc" | "growth" | "ipo" | "public";

export type UnderwriterTier = "bulge" | "boutique";

/** Why the campaign ended, when it did. "acquired" is the one non-failure exit:
 *  the founder cashes out and the score is the actual post-waterfall take.
 *  "folded" (P0): gave up in the garage — the napkins go in a drawer. */
export type DeathReason = "bankrupt" | "ousted" | "delisted" | "acquired" | "folded";

/** Stage ordering for gates ("growth"+ may acquire, etc.). Inserting "garage"
 *  below "angel" shifts absolute indices but every consumer compares RELATIVE
 *  order (stageAtLeast), so existing content minStage gates are unaffected. */
export const STAGE_ORDER: readonly CompanyStage[] = ["garage", "angel", "vc", "growth", "ipo", "public"];
export function stageAtLeast(stage: CompanyStage, floor: CompanyStage): boolean {
  return STAGE_ORDER.indexOf(stage) >= STAGE_ORDER.indexOf(floor);
}

// ---------------------------------------------------------------------------
// Content pack
// ---------------------------------------------------------------------------

/** Piecewise-linear curve point; `m` is monthIndex (Jan 1988 = 0). Clamped outside range. */
export interface CurvePoint {
  m: number;
  v: number;
}

export interface NodeDef {
  id: NodeId;
  displayName: string;
  featureNm: number;
  availableMonth: number;
  waferDiameterMm: number;
  waferCostStart: number;
  waferCostMature: number;
  waferCostTauMonths: number;
  maskSetCost: number;
  d0Intro: number; // defects/cm²
  d0Mature: number;
  d0TauMonths: number;
  gateDensity: number; // gates per mm²
  qual: { fee: number; engMonths: number; durationMonths: number };
  fabCycleMonths: number;
  perfIndex: number; // reserved for M1 multi-market competitiveness
}

export interface MarketDef {
  id: MarketId;
  displayName: string;
  pathAffinity: PathAffinity;
  /** TAM: units per month and reference ASP, piecewise-linear in monthIndex. */
  tamUnitsPerMonth: CurvePoint[];
  refAsp: CurvePoint[];
  /** Demand-side Moore's law: the complexity the market expects a competitive product to carry. */
  expectedKGatesBase: number; // at monthIndex 0
  expectedKGatesDoublingMonths: number;
  complexityFitExp: number; // quality ×= clamp((kGates/expected)^exp, min, max)
  complexityFitMin: number;
  complexityFitMax: number;
  baseCaptureShare: number;
  shareRampMonthly: number;
  productStaleMonths: number;
  staleDecayMonthly: number;
  nodeSensitivity: number;
  leadingEdgeLagMonths: number;
  unitsNoiseSigma: number;
  totalShareCap: number;
  testCostPerUnit: number;
  packageCostPerUnit: number;
  /** Baseline rival strength this market's share model was tuned against (M3):
   *  pressure = (1 + rivalryRef) / (1 + ΣrivalStrength) ⇒ 1 at the authored baseline. */
  rivalryRef: number;
  /** Optional per-market design-win lottery width (console sockets ≈ binary). */
  winRollSigma?: number;
  /** P0 segments (pivot spec §6): this market is a segment of one industry. */
  industryId?: string;
  /** Segment ASP anchor: competitiveness at which a product earns 1.0× refASP
   *  (defaults to models.aspQualityRef). A value segment prices value parts at
   *  its own — already low — reference price instead of punishing them twice. */
  aspQualityRef?: number;
  /** Attribute weights for archFit scoring; absent ⇒ archFit = 1 (legacy). */
  attrWeights?: Partial<AttrVector>;
  /** Month-keyed overrides for individual attr weights (pivot spec §7: segment
   *  requirement weights are era curves — D3D-mandatory-by-'99 lives here).
   *  A key present in this map supersedes its static attrWeights value. */
  attrWeightCurves?: Partial<Record<keyof AttrVector, CurvePoint[]>>;
  /** Selling here requires at least one of these tree nodes researched. */
  techGatesAnyOf?: TechNodeId[];
}

export interface SalaryPoint {
  m: number;
  design: number;
  verification: number;
  implementation: number;
}

/** Term-sheet generation (M2, design §3 Stage 2). The closing-hazard process is
 *  unchanged; what a "close" produces is now an offer the player must accept. */
export interface TermSheetDef {
  lifeMonths: number; // offer expires unanswered after this many months
  poolTopUpEarly: number; // option-pool refresh (frac of post-round fully diluted), rounds ≤ earlyRounds
  poolTopUpLate: number;
  earlyRounds: number;
  boardSeatMinAmount: number; // checks ≥ this bring a board seat
  valuationSigma: number; // lognormal noise on the pre-money
  confHighFloor: number; // board confidence ≥ this ⇒ preMoney × confHighBonus
  confHighBonus: number;
  confLowFloor: number; // board confidence < this ⇒ preMoney × confLowHaircut
  confLowHaircut: number;
  confCrisisFloor: number;
  confCrisisHaircut: number;
}

/** Board of directors (M2, design §3/§11 lite). Confidence 0–100 with quarterly
 *  meetings; consequences are financing-term haircuts, a hiring freeze, and —
 *  once investors control enough seats — founder ouster. */
export interface BoardDef {
  start: number;
  baseline: number; // confidence mean-reverts here
  meanReversionMonthly: number;
  meetingEveryMonths: number;
  concernFloor: number; // < this at a meeting ⇒ boardConcern + financing haircut
  ultimatumFloor: number; // < this at a meeting ⇒ hiring freeze
  freezeLiftFloor: number; // freeze lifts at a meeting once confidence recovers past this
  ousterFloor: number; // ≤ this at a meeting with enough investor seats ⇒ ousted
  ousterMinInvestorSeats: number;
  runwayHealthyMonths: number;
  runwayHealthyBonus: number;
  runwayFloorPenalty: number;
  /** Event-driven confidence deltas (keys match engine event semantics). */
  deltas: Record<string, number>;
}

/** IPO sequence (M2, design §3 Stage 3/4). */
export interface IpoDef {
  window: CurvePoint[]; // 0..1 IPO-window state (macro cycle consumer)
  minTtmRevenue: CurvePoint[]; // era-dependent revenue bar to file
  reqProfitQuarters: CurvePoint[]; // consecutive profitable quarters required (rounded)
  windowFloorToFile: number;
  pullFloor: number; // window below this mid-process ⇒ pulled IPO
  s1Months: number;
  roadshowMonths: number;
  pricingMaxMonths: number; // auto-price at mid if the player stalls
  roadshowProductivityHit: number; // company-wide EM haircut while management is on planes
  s1Cost: CurvePoint[];
  newSharesFrac: number; // primary shares sold, as fraction of post-IPO total
  pricingRangeSpread: number; // range = mid × (1 ± spread)
  lockupMonths: number;
  lockupPressure: number; // one-time price multiplier at lockup expiry
  cooldownMonths: number; // after a pulled IPO
  popCoeff: number; // first-day return per unit of underpricing
  windowHeatPop: number; // extra mean pop in a hot window
  firstDaySigma: number;
  underwriters: Record<UnderwriterTier, { feePct: number; multBonus: number; credStart: number }>;
}

/** Public-company layer (M2, design §3 Stage 5 / §10). */
export interface StreetDef {
  psMultiple: CurvePoint[]; // sector P/S multiple through the cycle
  peMultiple: CurvePoint[]; // sector P/E multiple through the cycle (prices TTM earnings when they beat the sales story)
  sentimentBase: number; // sentiment factor = base + slope × sentiment(m)
  sentimentSlope: number;
  credBase: number; // credibility factor = base + slope × credibility
  credSlope: number;
  guideFairValueCoeff: number; // how much guided growth is priced in now
  guideGrowthClampLo: number;
  guideGrowthClampHi: number;
  priceReversion: number; // monthly pull toward fair value
  priceSigma: number; // lognormal monthly noise
  beatThreshold: number; // actual/guide ≥ this ⇒ beat
  missThreshold: number; // actual/guide < this ⇒ miss
  beatCredDelta: number;
  inlineCredDelta: number;
  missCredDelta: number;
  beatPricePop: number;
  missPricePenalty: number;
  guidePolicySpan: number; // guide = forecast × (1 + policy × span)
  growthClampLo: number; // internal-forecast q/q growth clamp
  growthClampHi: number;
  guideGrowthDecay: number; // fraction of observed q/q growth extrapolated into the forecast (analysts model deceleration)
  delistPrice: number;
  delistWarnMonths: number;
  delistMonths: number;
  secondaryDiscount: number;
  secondaryFee: number;
  secondaryWindowFloor: number;
  secondaryCooldownMonths: number;
  secondaryMaxCapFrac: number; // the market absorbs at most this × market cap per offering
  buybackPremium: number;
  publicCostMonthly: CurvePoint[]; // listing/IR/audit; steps up at SOX (2002)
}

/** Distribution-channel inventory (M2, design §10). Sell-in books revenue early;
 *  the channel destocks in downturns and your revenue goes to zero first — and
 *  price protection makes HOLDING inventory cost real money as ASPs erode. */
export interface ChannelDef {
  maxMonths: number; // policy dial ceiling (months of forward demand held in channel)
  fillRate: number; // channel absorbs at most this × end-demand extra per month
  shareBonus: number; // availability bonus to target share at policy ≥ 1
  /** Fraction of the channel's ASP decline you credit back (price protection). */
  priceProtectRate: number;
  /** Distributor return rights: EOL'ing a product credits back this fraction of
   *  the stranded channel stock at its last ASP — booked revenue comes home. */
  eolReturnRate: number;
}

/** A dated historical news card (design §12 tier 1). Purely informational —
 *  mechanical macro effects live in the curves (sentiment, window, TAM, costs). */
export interface HistoryEventDef {
  id: string;
  month: number;
  headline: string;
  body: string; // the "what really happened" footnote
}

// ---------------------------------------------------------------------------
// M3 content: rivals, talent, stakeholders/asks, tools/IP, M&A
// ---------------------------------------------------------------------------

export type RivalArchetype = "berserker" | "integrator" | "highflyer" | "patient" | "startup";

export interface RivalMarketDef {
  marketId: MarketId;
  enterMonth: number;
  exitMonth?: number;
}

/** An AI rival (design §7.4): loose historical archetypes on rails + procedural
 *  spawns. Rivals never run the full economy — presence is a strength process. */
export interface RivalDef {
  id: string;
  displayName: string;
  archetype: RivalArchetype;
  path: CompanyPath | "mixed";
  foundedMonth: number;
  baseStrength: number; // peak-form presence scalar
  cadenceMonths: number; // product-cycle length (freshness sawtooth)
  fundingBeta: number; // scale sensitivity to market sentiment
  deathHazardBase: number; // monthly hazard floor once weak
  fateWindow?: { from: number; to: number; hazard: number }; // authored implosion
  acquirableFrom?: number;
  markets: RivalMarketDef[];
}

export interface RivalsContent {
  roster: RivalDef[];
  proc: {
    names: string[];
    baseStrength: number;
    cadenceMonths: number;
    fundingBeta: number;
    deathHazardBase: number;
    spawnHazard: number; // per-hot-market monthly
    maxAlive: number;
    acquirableAfterMonths: number;
  };
  newsPools: { spawn: string[]; death: string[]; release: string[]; acquired: string[] };
}

/** Talent-market tuning (design §9): heat, morale, comp, stars, poaching. */
export interface TalentDef {
  heat: CurvePoint[]; // 0..1 labor-market demand heat
  starNames: string[];
  moraleStart: number;
  moraleBaseline: number;
  moraleReversionMonthly: number;
  compPayrollSpan: number; // payroll × (1 + span × policy)
  compMoraleSpan: number; // morale baseline shift at policy ±1
  layoffMoraleCoef: number; // hit = min(max, coef × fired/headcount)
  layoffMoraleHitMax: number;
  downRoundMoraleHit: number;
  ipoMoraleBoost: number;
  underwaterPriceFrac: number; // "underwater" = price < frac × ipoPrice
  underwaterMoraleDrift: number; // monthly morale delta while underwater
  moraleAttritionCoef: number; // mult = clamp(1 + (baseline − morale) × coef, lo, hi)
  moraleAttritionLo: number;
  moraleAttritionHi: number;
  moraleProdCoef: number; // mult = clamp(1 + (morale − baseline) × coef, lo, hi)
  moraleProdLo: number;
  moraleProdHi: number;
  heatAttritionCoef: number; // attrition × (1 + coef × heat)
  heatRecruitCoef: number; // recruiting fee × (1 + coef × heat)
  /** Below-market offers don't fill in a hot labor market (design §9):
   *  fill = clamp(1 + (compCoef + heatCoef × heat) × min(0, compPolicy), floor, 1). */
  hireFillCompCoef: number;
  hireFillHeatCoef: number;
  hireFillFloor: number;
  poachHazardBase: number;
  poachClusterFrac: number; // fraction of the target pool per raid
  poachClusterMax: number;
  poachCounterCostMonths: number; // retention = months of salary × count
  poachCounterMoraleHit: number; // comp envy in the rest of the org
  poachLossMoraleHit: number;
  starOfferHazard: number;
  starMax: number;
  starSigningCost: CurvePoint[];
  starSalaryMult: number;
  starEffHeadsDigital: number;
  starEffHeadsAnalog: number; // analog gurus: rarer, heavier (design §5)
  starAnalogRarity: number; // offer-hazard multiplier on the analog path
  starPoachHazard: number; // monthly per star, × heat
  starCounterCostMonths: number;
  liftOutFrom: number; // the 2002 buyer's-market window
  liftOutTo: number;
  liftOutHazard: number;
  liftOutSentimentCeil: number;
  liftOutSizeMin: number;
  liftOutSizeMax: number;
  liftOutCostPerHead: number;
  integrationMonths: number; // post-acquisition window
  integrationAttritionMult: number;
  integrationMoraleHit: number;
}

/** Foundry + customer meters (design §11 — the unified stakeholder schema). */
export interface StakeholderDef {
  foundryStart: number;
  foundryBaseline: number;
  foundryReversionMonthly: number;
  /** Event-driven deltas: nodeQualified, tapeout, forecastBust. */
  foundryDeltas: Record<string, number>;
  forecastBustDropFrac: number; // q/q product-revenue drop that reads as a bust
  hotLotRelFloor: number; // rel ≥ this ⇒ fab-cycle and wafer perks
  hotLotCycleMonths: number;
  hotLotWaferDiscount: number;
  penaltyRelCeil: number; // rel < this ⇒ fab-cycle and wafer penalties
  penaltyCycleMonths: number;
  penaltyWaferMarkup: number;
  crunch: CurvePoint[]; // 0/1 capacity-crunch windows (1988-89, '95, '00)
  crunchSqueezeRelFloor: number; // rel < this during a crunch ⇒ allocation squeeze
  crunchSqueezeFactor: number; // demand you can actually supply
  customerStart: number;
  customerBaseline: number;
  customerReversionMonthly: number;
  /** fieldFailure, errata, eolGrowing, freshSilicon. */
  customerDeltas: Record<string, number>;
  supportProgramEm: number; // extra sustaining EM per dedicated-support program
  askMaxOpen: number; // cap on simultaneous open CONTENT asks
}

/** One closed effect vocabulary shared by authored and system-generated asks. */
export interface AskEffects {
  cash?: number; // integer dollars, negative = you pay
  /** Ledger line for the cash effect (system asks: retention/signingBonus/liftOut). */
  cashCategory?: "askCash" | "retention" | "signingBonus" | "liftOut";
  board?: number;
  foundry?: number;
  customer?: number; // applies to the ask's bound market
  morale?: number;
  hires?: { discipline: Discipline; count: number }[]; // instant productive heads
  losePool?: { discipline: Discipline; count: number };
  starJoin?: { name: string; discipline: Discipline; effHeads: number; salaryMult: number };
  starLeave?: string; // star id
  waferDiscount?: { pct: number; months: number };
  volumeCommit?: { unitsPerMonthFrac?: number; unitsPerMonth?: number; months: number; penaltyPerUnit: number };
  shareBoost?: { mult: number; months: number; marketId?: MarketId };
  supportProgram?: boolean; // bind to the ask's market
  sellCompany?: { price: number }; // accept an acquisition approach
  toolQualityHit?: { discipline: Discipline; amount: number }; // key-person loss
}

export type AskSource = "board" | "foundry" | "customer" | "talent" | "rival" | "flavor";

export interface AskOptionDef {
  label: string;
  effects: AskEffects;
}

/** An authored decision card (design §11 asks / §12 tiers 2-3). */
export interface AskDef {
  id: string;
  source: AskSource;
  headline: string;
  body: string;
  footnote?: string;
  trigger: {
    fromMonth?: number;
    toMonth?: number;
    minStage?: CompanyStage;
    hazardMonthly?: number; // omit ⇒ deterministic the first month conditions hold
    minRel?: number; // gate on the source's meter
    maxRel?: number;
    requiresProducts?: boolean;
    requiresPublic?: boolean;
    requiresStar?: boolean;
    cooldownMonths?: number;
    oncePerGame?: boolean;
  };
  expiresMonths: number;
  defaultOption: number; // applied on expiry
  cashScale?: "eraCapital" | "burn"; // resolve option cash at spawn
  options: AskOptionDef[];
}

/** Tools & methodology market (design §6.3). */
export type EdaTier = "basic" | "standard" | "premium";

export interface ToolsDef {
  edaTiers: Record<EdaTier, { costMult: number; methodMult: number }>;
  inhouseTauEm: number; // quality = 1 − e^(−em/τ)
  licenseSavingsMax: number; // per-discipline seat-cost reduction at quality 1
  prodBonusMax: number; // per-discipline productivity bonus at quality 1
  decayMonthlyUnstaffed: number; // em decay when the tool team is disbanded
  obsolescence: {
    month: number;
    discipline: Discipline;
    factor: number; // em ×= factor (the methodology shifted under you)
    headline: string;
    body: string;
  }[];
}

/** Licensable star/foundation IP (design §6.4). */
export interface IpDef {
  id: string;
  displayName: string;
  kind: "cpu" | "dsp" | "interface" | "foundation";
  availableFrom: number;
  kGatesFrac: number; // fraction of the chip you no longer design
  upfront: CurvePoint[];
  royaltyPct: number; // of that product's revenue
  lambdaMult: number; // integration-bug multiplier (bargain IP > 1)
  pathFit: PathAffinity;
}

// ---------------------------------------------------------------------------
// P0 content: the per-market tech tree + garage stage (SPEC_TECH_TREE_PIVOT.md §4/§3)
// ---------------------------------------------------------------------------

export type TechNodeId = string;
export type ResearchGrade = "A" | "B" | "C";

/** Product architecture attribute vector (pivot spec §7). Dimensions are scored
 *  against the TREE FRONTIER (what a company that researched everything on
 *  schedule would field), so values are relative, not absolute. Cost is NOT a
 *  dimension in P0 — feature ambition prices itself through kGates/die area. */
export interface AttrVector {
  perf3d: number;
  perf2d: number;
  features: number;
  compat: number;
  efficiency: number;
}
export const ATTR_KEYS: readonly (keyof AttrVector)[] = ["perf3d", "perf2d", "features", "compat", "efficiency"];

/** An architecture-tree node (pivot spec §4.1). The world gate (availableMonth)
 *  is when the idea exists; research is how a company earns access to it. */
export interface TechNodeDef {
  id: TechNodeId;
  displayName: string;
  tier: number;
  prereqs: TechNodeId[];
  /** Not required, but holding one discounts this node's EM cost. */
  softPrereqs?: { id: TechNodeId; discount: number }[];
  availableMonth: number;
  /** Researchable with founder-months in the garage (tier-0 theses + basics). */
  garageResearchable?: boolean;
  emCost: number;
  /** Which pools contribute company-phase research EM (fractions, sum ≈ 1). */
  disciplineMix: Record<Discipline, number>;
  /** Soft exclusivity: researching a second member of a group costs ×penalty
   *  (organizational unlearning) — nothing is permanently locked. */
  exclusiveGroup?: string;
  /** Contribution to a product's attribute vector when part of its architecture. */
  attrs?: Partial<AttrVector>;
  /** Holding this node satisfies a segment's techGatesAnyOf. */
  segmentAccess?: MarketId[];
  /** Month-keyed pitch value of holding this node (garage-stage theses). */
  pitchAppeal?: CurvePoint[];
  /** The world shifts under the node: its attrs multiply by factor from month on. */
  obsolescence?: { month: number; factor: number; headline: string; body: string };
  /** A/B/C breakthrough-roll weights; omitted ⇒ economy.tree.gradeWeights. */
  gradeWeights?: [number, number, number];
  /** Pivot salvage: completing this node banks EM credits toward these (spec §4.4). */
  creditsTo?: { id: TechNodeId; frac: number }[];
  /** One-time cash cost charged when research completes (e.g. the hardware
   *  emulator purchase — graphics_history.md §5.7). Curve: it gets cheaper. */
  cashCost?: CurvePoint[];
  /** Completing this node boosts every future project's verification at tape-out
   *  (coverage-space boost, like respinCoverageBoost) — the emulation gambit. */
  tapeoutCoverageBoost?: number;
}

export interface TechTreeDef {
  id: string;
  displayName: string;
  nodes: TechNodeDef[];
}

export type FounderArchetype = "technologist" | "operator" | "dealmaker";
export type GarageActivity = "research" | "demo" | "discovery" | "consulting" | "networking" | "recruiting";
export const GARAGE_ACTIVITIES: readonly GarageActivity[] = [
  "research", "demo", "discovery", "consulting", "networking", "recruiting",
];

/** Scenario garage block (pivot spec §2/§3). Present ⇒ the campaign starts
 *  pre-incorporation; absent ⇒ legacy incorporated start. */
export interface GarageDef {
  founders: { name: string; archetype: FounderArchetype; discipline: Discipline; savingsMonths: number }[];
  /** Per-founder monthly personal burn while full-time (the ramen constant). */
  personalBurnMonthly: number;
  /** Shared pot at day zero (added to pooled savings). */
  potStart: number;
}

/** Garage-stage tuning (economy content). */
export interface GarageCfg {
  /** Founder-month contribution while day-jobbing (savings exhausted). */
  dayJobEff: number;
  demoStages: number;
  demoEmPerStage: number;
  discoveryEmPerSegment: number;
  /** qualityExec bonus on your first product in a discovered segment. */
  discoveryFitBonus: number;
  /** Consulting income per founder-month, $/fm (era curve). */
  consultingRatePerFm: CurvePoint[];
  networkingEmPerLevel: number;
  warmIntroMax: number;
  recruitEmPerHire: number;
  recruitMax: number;
  /** Archetype effectiveness bonuses on their home activity. */
  dealmakerNetworkingMult: number;
  operatorRecruitingMult: number;
  /** Technologist presence shifts research grade weight from C toward A. */
  technologistGradeShift: number;
  pitch: {
    /** Era-honest seed check and pre-money bases (× pitchScore). */
    baseAmount: CurvePoint[];
    basePreMoney: CurvePoint[];
    amountMin: number;
    amountMax: number;
    /** Multipliers building pitchScore (spec §5.1). */
    demoMult: number; // × (1 + demoMult × demoStage/demoStages)
    introMult: number; // × (1 + introMult × warmIntro), cold call = coldCallFactor
    coldCallFactor: number;
    teamPerHireMult: number; // × (1 + teamPerHireMult × committedHires)
    /** Below this score the pitch fails outright (no sheet). */
    scoreFloor: number;
    valuationSigma: number;
    /** Each failed pitch stings the next for a while (the Valley talks). */
    failStingFactor: number;
    failStingMonths: number;
    cooldownMonths: number;
    incorporationCost: number;
  };
}

/** Tree research tuning (economy content). */
export interface TreeCfg {
  gradeWeights: [number, number, number]; // A / B / C
  gradeAttrMult: Record<ResearchGrade, number>;
  exclusivePenaltyMult: number;
  /** The frontier assumes the world adopts a node ~this long after it exists. */
  frontierLagMonths: number;
}

/** M&A (locked decision #5: the bundle model). */
export interface MaDef {
  rivalPremium: number; // price mult for a healthy target
  distressedDiscount: number; // price mult when weak/imploding
  scaleTeamSize: Record<Discipline, number>; // heads per unit of rival scale
  starChance: number; // a star rides along, × scale
  stumbleChance: number; // integration stumble probability
  stumbleShareFactor: number; // acquired share kept on a stumble
  minStageToBuy: CompanyStage;
  cheapTtmMult: number; // public: approaches when marketCap < mult × TTM
  approachHazardPublicCheap: number;
  hotSentimentFloor: number; // private approaches arrive in hot eras
  approachHazardPrivate: number;
  approachMinTtm: number;
  privatePremium: number;
  publicPremium: number;
  approachCooldownMonths: number;
  approachExpiresMonths: number;
}

export interface EconomyDef {
  /** Annual salaries by discipline, piecewise-linear in monthIndex. */
  salaries: SalaryPoint[];
  overheadBurden: number;
  toolCostPerEngYear: CurvePoint[];
  gnaBaseMonthly: CurvePoint[]; // era-scaled fixed overhead (rent, legal, admin)
  gnaPerHeadMonthly: number;
  hireRampMonths: number;
  rampEffectiveness: number;
  attritionMonthly: number;
  recruitingFeePctAnnual: number;
  severanceMonths: number;
  sentiment: CurvePoint[]; // 0..1
  eraCapitalScale: CurvePoint[];
  financing: {
    legalCost: number;
    /** A raise runs as a monthly closing-hazard process; withdrawn after maxMonths. */
    maxMonths: number;
    cooldownSuccessMonths: number;
    cooldownFailMonths: number;
    readinessPreSilicon: number;
    readinessWorkingSilicon: number;
    readinessRevenue2M: number;
    readinessRevenue10M: number;
    /** P0 (pivot spec, TUNING_P0 §3a): a tree position IS traction — a
     *  pre-silicon company holding this many researched nodes reads as more
     *  fundable than a deck-only startup. */
    readinessTreePosition: number;
    readinessTreeNodes: number;
    /** Story fatigue: the Valley funds the same story only so many times.
     *  From this many completed rounds with TTM below the threshold, readiness
     *  takes the factor haircut — working silicon with years of no traction
     *  stopped being fundable when the gold rush ended. */
    storyFatigueRounds: number;
    storyFatigueTtm: number;
    storyFatigueFactor: number;
    /** monthly hazard = clamp(pBase + pSlope × sentiment × readiness, pMin, pMax) */
    pBase: number;
    pSlope: number;
    pMin: number;
    pMax: number;
  };
  /** NRE/design-services consulting (design plan §3 Stage 1): revenue per engineer-month billed. */
  nre: {
    billRatePerEngMonth: CurvePoint[];
    maxEngineers: number; // demand-side cap on billable heads
  };
  termSheets: TermSheetDef;
  board: BoardDef;
  ipo: IpoDef;
  street: StreetDef;
  channel: ChannelDef;
  talent: TalentDef;
  stakeholders: StakeholderDef;
  tools: ToolsDef;
  ma: MaDef;
  /** P0: garage-stage and tech-tree tuning (pivot spec §3/§4). */
  garage: GarageCfg;
  tree: TreeCfg;
  models: ModelConstants;
}

export interface ModelConstants {
  methodologyGrowthPerYear: number; // 1.06
  /** Extra verification-productivity growth (testbenches → coverage → CRV → emulation). */
  verifMethodologyExtraPerYear: number; // 1.05
  designEmCoeff: number; // 1.6
  designEmExp: number; // 0.9
  methodologyDesignExp: number; // 0.7
  implEmRatio: number; // 0.55
  verifScaleRatio: number; // 0.55
  critLambdaCoeff: number; // 0.25
  critLambdaExp: number; // 0.75
  minorLambdaRatio: number; // 2.5
  feasMonths: number; // 3
  feasFitBase: number; // 0.75
  feasFitSpan: number; // 0.27
  feasFitTau: number; // 9
  dieOverhead: number; // 0.30
  yieldAlpha: number; // 3
  respinMaskFactor: number; // 0.5
  respinEscapeMult: number; // 0.3
  respinCoverageBoost: number; // 0.4
  respinFixEm: number; // 2
  respinFixMinMonths: number; // 1
  prodQualMonths: number; // 2
  prodQualMinImpl: number; // 2
  sustainingEmPerProduct: number; // 0.5
  implQualitySigma: number; // 0.05
  implQualityMin: number; // 0.85
  implQualityMax: number; // 1.15
  expPerShipped: number; // 0.05
  expCap: number; // 0.25
  /** Your D0 = baseline × (1 + gapIntro·e^(−researchEm/gapTauEm)): skimping research is a
   *  PERSISTENT defect-density penalty vs. the foundry's best-practiced customers. */
  yieldGapIntro: number; // 0.7
  yieldGapTauEm: number; // 18
  /** Field failures (design §6.5): shipping volume on an under-researched node risks recalls.
   *  hazard/mo = base × e^(−researchEm/gapTauEm) × min(2, √(units/unitRef)) */
  fieldFailBase: number; // 0.05
  fieldFailUnitRef: number; // 400000
  fieldFailCostPerUnit: number; // 2.5 (× ~a quarter of volume)
  fieldFailQualityHit: number; // 0.85 (permanent qualityExec multiplier)
  /** Block-parallelism model: a chip is kGates/blockKGates independent blocks, and
   *  the ideal team is staff-per-block × blocks — LINEAR in gate count within an era.
   *  Tools grow the complexity one block owner can span: blockKGates = base × M^exp. */
  blockKGatesBase: number; // 5 — a 1988 block is ~5 kGates
  blockKGatesMethodExp: number; // 1.0 — block capacity rides the methodology curve
  designersPerBlock: number; // 1.2
  implPerBlock: number; // 0.9
  verifPerBlock: number; // 0.5
  /** Diminishing returns: extra heads beyond ideal saturate toward (1+frac)×ideal effective. */
  teamOverflowFrac: number; // 0.75
  /** Starvation (the S-curve's low end): a skeleton crew on a many-block chip thrashes.
   *  Below starveKnee×(1−1/blocks) of the ideal team, per-head output ramps down toward
   *  starveFloor. A 1-block chip never starves — one owner per block is fine. */
  starveFloor: number; // 0.35
  starveKnee: number; // 0.45
  feasIdealStaff: number; // 4
  respinFixIdealStaff: number; // 3
  /** Design phase wants a small physical-design presence (floorplan/synthesis trials). */
  designPhaseImplFrac: number; // 0.25 — ideal impl during design, as frac of layout ideal
  implEarlyCapFrac: number; // 0.25 — impl EM bankable before tape-in, as frac of implEmTotal
  tapeinMinImplFrac: number; // 0.10 — tape-in requires this much impl done (floorplan exists)
  /** Layout needs some designers on call for ECOs/constraint questions. */
  layoutDesignSupportFrac: number; // 0.25 — ideal design staff during layout, as frac of design ideal
  layoutNoDesignEff: number; // 0.6 — layout speed with zero design support
  /** RTL bugs found during layout force a return to the design phase.
   *  λ_rework = coeff × critλ × (1 − covAtTapein) × escapeMult^reworks. */
  rtlReworkCoeff: number; // 0.2
  rtlReworkEmFrac: number; // 0.12 — fraction of designEmTotal to redo per rework
  layoutKeepFrac: number; // 0.65 — layout progress kept across a rework
  rtlReworkCoverageBoost: number; // 0.15 — the found bug improves the testbench
  rtlReworkEscapeMult: number; // 0.5 — remaining rework hazard after each rework
  errataQualityHaircut: number; // 0.04
  errataHaircutCap: number; // 3
  /** ASP multiplier = clamp((C/ref)^exp, min, max) — convex: clones get commodity prices. */
  aspQualityExp: number; // 1.8
  aspQualityRef: number; // 0.85 — era-typical well-executed product prices at 1.0× refASP
  aspQualityMin: number; // 0.35
  aspQualityMax: number; // 1.25
  aspAgeErosionMonthly: number; // 0.985 (multiplier per month of product age)
  winRollSigma: number; // 0.15
  winRollMin: number; // 0.6
  winRollMax: number; // 1.4
  incumbencyBonus: number; // 0.10
  incumbencyShareFloor: number; // 0.05
  targetShareOffset: number; // 0.25
  targetShareSpanCap: number; // 1.3
  targetShareExp: number; // 1.2
  /** Lognormal sigma on each project's true (hidden) bug rate vs. the public formula. */
  hiddenLambdaSigma: number; // 0.35
  /** Off-path projects (design §5): effort multiplier and requirements-fit multiplier. */
  offPathEffortMult: number; // 1.35
  offPathFitMult: number; // 0.88
  /** Public-supplier credibility (M2): big-OEM procurement favors suppliers with
   *  public balance sheets — a share-target bonus while listed. */
  publicCredibilityShareBonus: number; // 0.06
  /** Field-failure hazard now keys on per-node reliability EM (yield/reliability split, §6.5). */
  reliabilityTauEm: number; // 12
  /** Rival share pressure (M3, §7.4): target ×= clamp((1+rivalryRef)/(1+Σstrength), min, max). */
  rivalPressureMin: number; // 0.55
  rivalPressureMax: number; // 1.5
  /** Competitive markets also compress price: asp ×= pressure^rivalAspExp (clamped by pressure bounds). */
  rivalAspExp: number; // 0.35
  /** Map a rival's presence strength to an implied market share (revenue proxy + acquisition bundle). */
  rivalShareCoeff: number; // 0.11
  rivalShareCap: number; // 0.25
  /** Customer-relationship share factor: 1 + (rel − baseline) × slope, clamped (M3, §11). */
  customerRelSlope: number; // 0.004
  customerRelMin: number; // 0.8
  customerRelMax: number; // 1.15
  /** Architecture fit vs. the tree frontier (P0, pivot spec §7):
   *  target ×= clamp((Σw·min(ratio,ratioCap)/Σw)^exp, min, max); 1 for legacy
   *  markets (no attrWeights) or products with no architecture. */
  archFitExp: number; // 1.4
  archFitMin: number; // 0.35
  archFitMax: number; // 1.2
  archRatioCap: number; // 1.25 — being ahead of the frontier helps, boundedly
  /** archFit's dampened entry into segment PRICE (the crown premium / stale
   *  discount). 0 disables; legacy markets see archFit = 1 either way. */
  aspArchFitExp: number; // 0.8
  /** The frontier sums EVERY available node — an idealized company nobody is.
   *  This maps it down to a realistic era leader (calibration constant). */
  frontierDiscount: number; // 0.6
}

export interface ScenarioDef {
  id: string;
  startMonthIndex: number;
  endMonthIndex: number;
  monthsPerTick: number;
  /** Company cash at start (legacy incorporated scenarios). Ignored — and may
   *  be 0 — when a garage block is present: the seed round funds the company. */
  startingCash: number;
  /** Founding employees by discipline. For garage scenarios these land on
   *  incorporation day (the garage founders join their own company). */
  founders: Record<Discipline, number>;
  preQualifiedNodes: NodeId[];
  /** Founders' common shares at day zero — the cap-table denominator. */
  founderShares: number;
  /** P0 (pivot spec §2): present ⇒ start pre-incorporation in the garage. */
  garage?: GarageDef;
  /** The architecture tree this scenario plays on (ContentPack.trees id). */
  treeId?: string;
  /** Strategic development contracts (pivot spec §5.4/§8.1.1), thesis-keyed.
   *  P0-lite: auto-signed at incorporation when the thesis matches. */
  devContracts?: DevContractDef[];
}

/** A strategic principal's development contract (pivot spec §5.4) — the
 *  historical revenue bridge: NRE while you build, committed socket royalties
 *  inside an authored calendar window, and (quad only) the authored gen-2
 *  cancellation beat. P0-lite simplifications: signed automatically with the
 *  seed when the thesis matches; the architecture warp on chip 1 is priced as
 *  a flat design-EM tax rather than die-area obligations (P1). */
export interface DevContractDef {
  id: string;
  /** Offered to holders of this researched thesis at incorporation. */
  thesisId: TechNodeId;
  /** Flavor: who is paying ("the quad-native console principal"). */
  principal: string;
  /** No signing after this month — the socket's window has closed. */
  signByMonth: number;
  /** Monthly NRE payments from signing until the royalty window opens. */
  nreMonthly: number;
  /** Explicit end of the NRE phase; defaults to royaltyStartMonth (needed
   *  when the committed socket pays years after the engineering work ends). */
  nreEndMonth?: number;
  /** Design EM the principal's dated feature obligations consume monthly
   *  during the NRE phase (the P0-lite architecture-warp tax). */
  nreEmMonthly: number;
  /** Committed socket royalties inside [royaltyStartMonth, royaltyEndMonth]. */
  royaltyStartMonth: number;
  royaltyEndMonth: number;
  royaltyMonthly: number;
  /** Authored cancellation beat (the NV2 story): the principal kills the
   *  follow-up and pays a walk-away fee. Ends the contract permanently. */
  cancelMonth?: number;
  cancelFee?: number;
}

/** A signed development contract riding in game state. */
export interface ActiveDevContract {
  defId: string;
  signedMonth: number;
  /** True once the principal has walked away (nothing pays after). */
  cancelled: boolean;
}

export interface ContentPack {
  version: string;
  nodes: NodeDef[]; // sorted by availableMonth ascending
  markets: MarketDef[];
  economy: EconomyDef;
  scenario: ScenarioDef;
  /** Historical rail-event cards, sorted by month. */
  events: HistoryEventDef[];
  /** M3 ecosystem content. */
  rivals: RivalsContent;
  asks: AskDef[];
  ip: IpDef[];
  /** P0: architecture trees (pivot spec §4). */
  trees: TechTreeDef[];
}

// ---------------------------------------------------------------------------
// Game state
// ---------------------------------------------------------------------------

export interface EngineerPool {
  productive: number;
  ramping: { count: number; monthsLeft: number }[];
}

export interface PeopleState {
  pools: Record<Discipline, EngineerPool>;
  pendingHires: { discipline: Discipline; count: number }[];
}

export type NodeStatus = "locked" | "qualifying" | "qualified";

/** Rival runtime state. Procedural rivals carry their own def (content is static). */
export interface RivalState {
  alive: boolean;
  diedMonth: number | null;
  scale: number; // funding-driven muscle; strength = base × scale × freshness
  cyclePos: number; // months since last product refresh
  acquiredByPlayer: boolean;
  def?: RivalDef; // procedural spawns only
}

export interface StarEngineer {
  id: string;
  name: string;
  discipline: Discipline;
  effHeads: number; // extra effective heads in that discipline's EM budget
  salaryMult: number; // × band salary
  joinedMonth: number;
}

export interface TalentState {
  morale: number; // 0..100 company-wide
  compPolicy: number; // −1 below-market .. +1 above-market
  stars: StarEngineer[];
  integrationMonthsLeft: number; // post-acquisition churn window
}

export interface StakeholderMeters {
  foundryRel: number; // 0..100
  customers: Record<MarketId, number>; // 0..100 per market
  waferDiscountPct: number;
  waferDiscountMonthsLeft: number;
  volumeCommit: { unitsPerMonth: number; monthsLeft: number; penaltyPerUnit: number } | null;
  shareBoosts: { marketId: MarketId; mult: number; monthsLeft: number }[];
  supportPrograms: MarketId[]; // dedicated-support commitments (sustaining-EM tax)
  lastForecastBustMonth: number | null;
}

/** A decision on the table. Effects are fully resolved at spawn (authored cards
 *  resolve their cash scaling; system cards are born with computed numbers). */
export interface OpenAsk {
  id: string; // instance id, "ask-<n>"
  defId: string | null; // authored card id, or null for system-generated
  source: AskSource;
  headline: string;
  body: string;
  footnote?: string;
  marketId?: MarketId; // bound market for customer/shareBoost/support effects
  monthsLeft: number;
  defaultOption: number;
  options: { label: string; effects: AskEffects }[];
}

export interface ToolsState {
  edaTier: EdaTier;
  inhouse: Record<Discipline, { em: number; staff: number; announced: boolean }>;
}

/** Architecture-tree research state (P0, pivot spec §4.2). */
export interface TreeState {
  researched: Record<TechNodeId, { grade: ResearchGrade; month: number }>;
  inProgress: Record<TechNodeId, number>; // EM banked toward completion
  credits: Record<TechNodeId, number>; // pivot-salvage EM credits
  staffing: Record<TechNodeId, number>; // company-phase engineers per node
}

/** Pre-incorporation state (P0, pivot spec §3). Null once incorporated.
 *  P0 simplification vs. spec: savings are POOLED (one runway, all founders
 *  day-job together at zero) and garage research has a single focus node. */
export interface GarageState {
  founders: { name: string; archetype: FounderArchetype; discipline: Discipline }[];
  /** Pooled personal savings + pot, dollars. Burn = founders × personalBurn. */
  savings: number;
  demoEm: number;
  demoStage: number;
  discoveryEm: number;
  discovered: MarketId[];
  networkingEm: number;
  warmIntro: number;
  introSpent: number;
  recruitEm: number;
  committedHires: Record<Discipline, number>;
  researchNodeId: TechNodeId | null;
  alloc: Partial<Record<GarageActivity, number>>;
  lastPitchMonth: number | null;
  pitchesFailed: number;
}

/** What the garage leaves behind after incorporation (discovery bonuses). */
export interface GarageLegacy {
  discovered: MarketId[];
  demoStage: number;
}

export interface MaState {
  approachCooldownMonths: number;
}

export interface NodeAccessState {
  status: NodeStatus;
  qualEmRemaining: number;
  qualMonthsRemaining: number;
  /** Yield learning (design §6.5): closes the persistent D0 gap vs. the foundry baseline. */
  researchEm: number;
  researchStaff: number;
  /** Reliability engineering (design §6.5): qual rigor/burn-in; buys down field-failure hazard. */
  reliabilityEm: number;
  reliabilityStaff: number;
}

export type ProjectPhase =
  | "feasibility"
  | "design"
  | "waitingTapein"
  | "layout"
  | "waitingTapeout"
  | "fab"
  | "validation"
  | "respinDecision"
  | "respinFix"
  | "prodQual"
  | "shipped"
  | "cancelled";

/** Phases in which a project still consumes decisions/staff. */
export const LIVE_PHASES: readonly ProjectPhase[] = [
  "feasibility",
  "design",
  "waitingTapein",
  "layout",
  "waitingTapeout",
  "fab",
  "validation",
  "respinDecision",
  "respinFix",
  "prodQual",
];

export interface ProjectHidden {
  critLambda: number;
  minorLambda: number;
  escapeMult: number;
}

export interface ChipProject {
  id: ProjectId;
  marketId: MarketId;
  nodeId: NodeId;
  kGates: number;
  /** Licensed IP (design §6.4): you integrate these blocks instead of designing
   *  them. Effort/λ run on kGatesEffective; market complexity credit and die
   *  area stay on the full kGates; the product pays the royalty forever. */
  ipIds: string[];
  kGatesEffective: number;
  royaltyPct: number;
  /** Design partitions (kGatesEffective / era block size, snapshotted at start).
   *  Sets the ideal team size — parallelism is bounded by blocks to own. */
  blocks: number;
  startedMonth: number;
  /** True when the project's market disagrees with the company's locked path. */
  offPath: boolean;
  phase: ProjectPhase;
  staffing: Record<Discipline, number>;
  feasEmDone: number;
  feasMonthsLeft: number;
  designEmDone: number;
  designEmTotal: number;
  implEmDone: number;
  implEmTotal: number;
  verifEmDone: number;
  verifScale: number;
  /** Coverage snapshotted at the tape-in decision; drives the RTL-rework roll. */
  covAtTapein: number | null;
  /** Times layout kicked the project back to design (late RTL bugs). */
  rtlReworks: number;
  requirementsFit: number | null;
  implQualityRoll: number | null;
  dieAreaMm2: number | null;
  fabMonthsLeft: number;
  validationMonthsLeft: number;
  respinFixEmRemaining: number;
  respinFixMonthsLeft: number;
  prodQualMonthsLeft: number;
  respins: number;
  errataCount: number;
  prodQualStalled: boolean;
  respinFixStalled: boolean;
  /** Architecture (P0, pivot spec §7): tree nodes + grades snapshotted at start. */
  archNodes: { id: TechNodeId; grade: ResearchGrade }[];
  hidden: ProjectHidden;
}

export interface Product {
  id: string;
  projectId: ProjectId;
  marketId: MarketId;
  nodeId: NodeId;
  releasedMonth: number;
  /** Execution quality (fit × impl roll × errata haircut) — frozen at release. */
  qualityExec: number;
  designWinRoll: number;
  /** Reporting only: momentary competitiveness at release. */
  competitivenessAtRelease: number;
  share: number;
  kGates: number;
  dieAreaMm2: number;
  active: boolean;
  /** Units sitting in distribution (sell-in booked, not yet sold through). */
  channelUnits: number;
  /** Last realized ASP — price protection credits the channel when it falls. */
  lastAsp: number | null;
  /** IP royalty owed on this product's revenue (design §6.4). */
  royaltyPct: number;
  /** Came in an acquisition bundle (locked decision #5). */
  acquired: boolean;
  /** Architecture carried from the project; attrs computed live monthly so
   *  obsolescence riders age shipped products (P0, pivot spec §7). */
  archNodes: { id: TechNodeId; grade: ResearchGrade }[];
}

/** An open venture term sheet (design §3 Stage 2). All terms are player-visible. */
export interface TermSheet {
  round: number; // 1-based round it would close
  amount: number;
  preMoney: number;
  poolTopUpPct: number; // option-pool refresh, frac of post-round fully diluted
  boardSeat: boolean;
  downRound: boolean; // preMoney below the last post-money
  monthsLeft: number;
}

/** A closed financing round, as the cap table remembers it. */
export interface CapTableRound {
  name: string; // "Series A" ...
  month: number;
  amount: number;
  preMoney: number;
  postMoney: number;
  sharesIssued: number;
  poolSharesAdded: number;
  boardSeat: boolean;
  downRound: boolean;
}

export interface FinanceState {
  /** Shares ledger — founderShare is derived from it and stored for reporting. */
  founderShares: number;
  investorShares: number;
  poolShares: number;
  publicShares: number; // IPO primary + secondaries − buybacks
  totalRaised: number;
  founderShare: number;
  roundsCompleted: number;
  rounds: CapTableRound[];
  activeRaise: { ask: number; monthsActive: number } | null;
  openTermSheet: TermSheet | null;
  lastPostMoney: number; // 0 until a round closes (down-round detection)
  raiseCooldownMonths: number;
  secondaryCooldownMonths: number;
  everWorkingSilicon: boolean;
  ttmRevenue: number[]; // trailing ≤12 monthly totals, newest last
  burnHistory: number[]; // trailing ≤3 net outflows (excl. financing), newest last
  /** Founder's post-waterfall proceeds when the company is sold (M3 exits). */
  exitFounderTake: number | null;
}

export interface BoardState {
  confidence: number; // 0..100
  investorSeats: number;
  hiringFreeze: boolean; // board-imposed; hire commands are rejected while true
  lastMeetingMonth: number | null;
}

export type IpoPhase = "s1" | "roadshow" | "pricing";

export interface IpoProcess {
  phase: IpoPhase;
  monthsLeft: number;
  underwriter: UnderwriterTier;
  rangeLo: number | null; // set when pricing opens
  rangeHi: number | null;
}

export interface IpoState {
  process: IpoProcess | null;
  pulls: number;
  cooldownMonths: number;
  lockupMonth: number | null; // pending lockup-expiry month (set at pricing)
  ipoMonth: number | null;
  ipoPrice: number | null;
}

export interface StreetState {
  sharePrice: number;
  credibility: number; // 0..1 guidance credibility
  guidancePolicy: number; // −1 sandbag .. +1 stretch
  guidedQuarterRevenue: number | null; // the number the street holds you to
  lastQuarterRevenue: number;
  consecutiveMisses: number;
  belowListingMonths: number;
  priceHistory: { m: number; price: number; cap: number }[]; // trailing ≤260
}

export interface KpiSample {
  month: number;
  cash: number;
  revenue: number; // product + NRE
  nreRevenue: number;
  cogs: number;
  opex: number;
  headcount: number;
  share: number;
  runwayMonths: number; // capped at 999
  activeProjects: number;
  shippedProducts: number;
  shareByMarket: Record<MarketId, number>;
  headcountByDiscipline: Record<Discipline, number>;
}

export interface GameState {
  contentVersion: string;
  seed: number;
  tick: number;
  monthIndex: number;
  rng: [number, number, number, number];
  cash: number; // integer dollars
  dead: boolean;
  deathMonth: number | null;
  deathReason: DeathReason | null;
  /** Locked by the first project started in a path-affine market; null = trunk. */
  path: CompanyPath | null;
  stage: CompanyStage;
  people: PeopleState;
  /** Design engineers currently assigned to NRE consulting work. */
  nreEngineers: number;
  nodes: Record<NodeId, NodeAccessState>;
  projects: ChipProject[];
  products: Product[];
  markets: Record<MarketId, { totalShare: number; channelMonths: number }>;
  finance: FinanceState;
  board: BoardState;
  ipo: IpoState;
  street: StreetState | null; // null until priced
  /** M3 ecosystem state. */
  rivals: Record<string, RivalState>;
  talent: TalentState;
  stakeholders: StakeholderMeters;
  asks: OpenAsk[];
  /** Authored-ask spawn history: defId → last spawn month (cooldowns / once-per-game). */
  askLog: Record<string, number>;
  tools: ToolsState;
  ma: MaState;
  /** P0: architecture-tree research (empty maps for legacy campaigns). */
  tree: TreeState;
  /** P0: pre-incorporation state; null once incorporated (or for legacy starts). */
  garage: GarageState | null;
  /** What the garage left behind (discovery bonuses); null for legacy starts. */
  garageLegacy: GarageLegacy | null;
  /** P0: signed strategic development contract (spec §5.4); null when none. */
  devContract: ActiveDevContract | null;
  counters: { nextProject: number; shippedProjects: number; nextAsk: number; nextRival: number; nextStar: number };
  kpiHistory: KpiSample[];
}

// ---------------------------------------------------------------------------
// Commands
// ---------------------------------------------------------------------------

export type Command =
  | { type: "hire"; discipline: Discipline; count: number }
  | { type: "fire"; discipline: Discipline; count: number }
  | { type: "setNre"; engineers: number }
  | { type: "startNodeQual"; nodeId: NodeId }
  | { type: "setYieldResearch"; nodeId: NodeId; engineers: number }
  | { type: "setReliability"; nodeId: NodeId; engineers: number }
  | { type: "startProject"; marketId: MarketId; nodeId: NodeId; kGates: number; ipIds?: string[]; archNodeIds?: TechNodeId[] }
  | { type: "setStaffing"; projectId: ProjectId; staffing: Record<Discipline, number> }
  | { type: "tapeIn"; projectId: ProjectId }
  | { type: "tapeOut"; projectId: ProjectId }
  | { type: "respin"; projectId: ProjectId }
  | { type: "cancelProject"; projectId: ProjectId }
  | { type: "endOfLifeProduct"; productId: string }
  | { type: "raiseRound"; ask: number }
  | { type: "acceptTermSheet" }
  | { type: "rejectTermSheet" }
  | { type: "fileS1"; underwriter: UnderwriterTier }
  | { type: "priceIpo"; perShare: number }
  | { type: "setGuidance"; policy: number }
  | { type: "secondaryOffering"; amount: number }
  | { type: "buyback"; amount: number }
  | { type: "setChannelPolicy"; marketId: MarketId; months: number }
  | { type: "answerAsk"; askId: string; option: number }
  | { type: "setCompPolicy"; policy: number }
  | { type: "setEdaTier"; tier: EdaTier }
  | { type: "setToolStaff"; discipline: Discipline; engineers: number }
  | { type: "acquireRival"; rivalId: string; payment: "cash" | "stock" }
  | { type: "setGarageAlloc"; alloc: Partial<Record<GarageActivity, number>>; researchNodeId?: TechNodeId | null }
  | { type: "schedulePitch" }
  | { type: "foldCompany" }
  | { type: "setTreeStaff"; nodeId: TechNodeId; engineers: number };

// ---------------------------------------------------------------------------
// Reporting
// ---------------------------------------------------------------------------

export type LedgerCategory =
  | "payroll"
  | "tools"
  | "gna"
  | "recruiting"
  | "severance"
  | "maskNre"
  | "nodeQualFee"
  | "financingLegal"
  | "cogs"
  | "recall"
  | "revenue"
  | "nre"
  | "financing"
  | "s1Costs"
  | "ipoProceeds"
  | "ipoFees"
  | "secondary"
  | "buyback"
  | "publicCompany"
  | "priceProtection"
  | "channelReturns"
  | "ipUpfront"
  | "ipRoyalty"
  | "retention"
  | "signingBonus"
  | "liftOut"
  | "acquisition"
  | "volumePenalty"
  | "askCash"
  | "incorporation"
  | "treeCash"
  | "devContract"
  | "socketNre";

export interface LedgerEntry {
  month: number;
  category: LedgerCategory;
  amount: number; // integer; positive = inflow
  note: string;
}

export type EngineEventType =
  | "projectStarted"
  | "feasibilityComplete"
  | "designComplete"
  | "tapein"
  | "designRework"
  | "layoutComplete"
  | "tapeout"
  | "siliconWorks"
  | "siliconErrata"
  | "siliconDead"
  | "respinStarted"
  | "productReleased"
  | "qualComplete"
  | "roundClosed"
  | "roundFailed"
  | "projectCancelled"
  | "prodQualStalled"
  | "respinFixStalled"
  | "fieldFailure"
  | "productEol"
  | "pathLocked"
  | "stageAdvanced"
  | "boardConcern"
  | "termSheet"
  | "termSheetExpired"
  | "boardMeeting"
  | "boardUltimatum"
  | "founderOusted"
  | "s1Filed"
  | "roadshowStarted"
  | "ipoPricing"
  | "ipoPriced"
  | "ipoPulled"
  | "lockupExpired"
  | "earnings"
  | "secondaryCompleted"
  | "buybackCompleted"
  | "delistWarning"
  | "delisted"
  | "history"
  | "rivalSpawned"
  | "rivalDied"
  | "rivalNews"
  | "rivalAcquired"
  | "poachRaid"
  | "starOffer"
  | "starJoined"
  | "starLeft"
  | "liftOutOffer"
  | "askOpened"
  | "askResolved"
  | "askExpired"
  | "acquisitionOffer"
  | "companyAcquired"
  | "integrationDone"
  | "integrationStumble"
  | "toolShipped"
  | "toolObsoleted"
  | "foundrySqueeze"
  | "bankrupt"
  | "treeResearched"
  | "treeObsoleted"
  | "garageBeat"
  | "pitchResult"
  | "incorporated"
  | "folded"
  | "devContractSigned"
  | "devContractCancelled";

export interface EngineEvent {
  month: number;
  type: EngineEventType;
  message: string;
  data: Record<string, number | string | boolean | null>;
}

/** Per-product market actuals for one tick (observability: ties 1:1 to the ledger). */
export interface ProductSaleSample {
  productId: string;
  marketId: MarketId;
  units: number; // sell-in (shipments — what revenue books on)
  endUnits: number; // sell-through (customer consumption)
  channelUnits: number; // inventory sitting in distribution after this tick
  asp: number; // realized average selling price
  unitCost: number;
  revenue: number; // integer $, matches the ledger entry exactly
  cogs: number; // integer $, matches the ledger entry exactly
  recall: number; // integer $ recall charge this tick (0 if none)
  priceProtection: number; // integer $ channel price-protection credit this tick
}

/** Per-discipline engineer-month accounting for one tick (invariant 4 observability). */
export interface AllocationReport {
  budget: number; // (productive + rampEffectiveness × ramping) × monthsPerTick
  nre: number;
  sustaining: number;
  qual: number;
  research: number;
  reliability: number;
  /** In-house tool development (M3, design §6.3). */
  tools: number;
  /** Architecture-tree research (P0, pivot spec §4.2). */
  tree: number;
  byProject: Record<ProjectId, number>;
  idle: number;
}

export interface TickReport {
  month: number;
  ledger: LedgerEntry[];
  events: EngineEvent[];
  rejectedCommands: { command: Command; reason: string }[];
  allocation: Record<Discipline, AllocationReport>;
  productSales: ProductSaleSample[];
  kpi: KpiSample;
}

export interface StepResult {
  state: GameState;
  report: TickReport;
}

// ---------------------------------------------------------------------------
// Player view (hidden info stripped)
// ---------------------------------------------------------------------------

export type ViewProject = Omit<ChipProject, "hidden"> & {
  /** Player-visible coverage-confidence gauge: 1 − exp(−verifEmDone/verifScale). */
  coverage: number;
};

export interface PlayerView {
  contentVersion: string;
  tick: number;
  monthIndex: number;
  cash: number;
  dead: boolean;
  deathReason: DeathReason | null;
  path: CompanyPath | null;
  stage: CompanyStage;
  people: PeopleState;
  nreEngineers: number;
  nodes: Record<NodeId, NodeAccessState>;
  projects: ViewProject[];
  products: Product[];
  markets: Record<MarketId, { totalShare: number; channelMonths: number }>;
  finance: FinanceState;
  board: BoardState;
  ipo: IpoState;
  street: StreetState | null;
  rivals: Record<string, RivalState>;
  talent: TalentState;
  stakeholders: StakeholderMeters;
  asks: OpenAsk[];
  tools: ToolsState;
  ma: MaState;
  tree: TreeState;
  garage: GarageState | null;
  garageLegacy: GarageLegacy | null;
  devContract: ActiveDevContract | null;
  counters: { nextProject: number; shippedProjects: number; nextAsk: number; nextRival: number; nextStar: number };
  kpiHistory: KpiSample[];
}
